import type { CSSVarFunction } from '@vanilla-extract/private';
import {
  densityVars,
  scaleVars
} from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { rgb as toRgbString } from '../utils/PRIVATE_colorUtils';
// Importing this here so we can export it as extendThemeContractVars for readability
import { mergeDeepRight as extendThemeContractVars } from 'ramda';

// Added: new types
type RGBType = `rgb(${CSSVarFunction})`;
type RGBAType = `rgba(${CSSVarFunction}, ${number})`;
type ColorMixType = `color-mix(in srgb, ${string} ${string}, ${string})`;

const rgb = (rgb: CSSVarFunction) => toRgbString(rgb) as RGBType;
// TODO: create rgba function in PRIVATE_colorUtils
const rgba = (rgb: CSSVarFunction, alpha: number): RGBAType => `rgba(${rgb}, ${alpha})`;
const colorMix = (mixColor: string, mixPercent: string, baseColor: string): ColorMixType =>
  `color-mix(in srgb, ${mixColor} ${mixPercent}, ${baseColor})`;

// JET-59344 refactor of 'fallbackVar' from '@vanilla-extract/css'.
// directly importing the function was causing vanilla-extract to be bundled into
// the preact packages and causing issues with testing
const fallbackVar = (...values: [string, ...Array<string>]): CSSVarFunction => {
  const finalValue = values.reverse().reduce((acc: string, value: string) => {
    if (acc === '') {
      acc = String(value);
    } else {
      if (typeof value !== 'string' || !/^var\(--.*\)$/.test(value)) {
        throw new Error(`Invalid variable name: ${value}`);
      }

      acc = value.replace(/\)$/, `, ${acc})`);
    }
    return acc;
  }, '');

  return finalValue as CSSVarFunction;
};

// For the default, where no scale var is specified, we use rem units, so that it will scale based on the root font-size.
const scaleFallback = () => fallbackVar(scaleVars.size.units, '0.25rem');
const xUnits = (unit: number) =>
  unit === 1 ? scaleFallback() : `calc(${unit} * ${scaleFallback()})`;

// For the default, where no density var is specified, we set it to 0px.
const densityFallback = () => fallbackVar(densityVars.density.units, '0px');
const densityUnits = (unit: number) =>
  unit === 1 ? densityFallback() : `calc(${unit} * ${densityFallback()})`;

const min = (val: string, minVal: string): string => `min(${val},${minVal})`;
const max = (val: string, minVal: string): string => `max(${val},${minVal})`;

export {
  colorMix,
  rgb,
  rgba,
  xUnits,
  densityUnits,
  fallbackVar,
  min,
  max,
  extendThemeContractVars
};

export type { RGBType, RGBAType };
